import { COOKIE_KEY } from '@/constants/config';
import { isEmpty, pick } from 'lodash-es';
import type { LoginInfo, Loose, Session, UserInfo } from '~/api/interface';
import type { UserPublishItem } from '~/api/interface/publish';
import {
  getUserAccountList,
  getUserInfo,
  loginForWeb,
  logoutForBe,
  logoutForWeb,
} from '~/api/module/auth';
import { yfxLogin } from '~/utils/track';

type ShowAccountModal = 'login' | 'reset' | 'select' | 'register' | null;

// 弹窗类型
export const useAccountType = () => {
  return useState<ShowAccountModal>('showAccountType', () => null);
};
// 用户账号列表
export const useAccountList = () => {
  return useState<LoginInfo[]>('accountList', () => []);
};

export const useCurrentAccount = () => {
  const session = useSession();
  const accountList = useAccountList();
  return accountList.value.find(item => item.user_id === session.value?.user_id);
};

// 不可创建账号弹窗
export const useCheckCreate = () => {
  return useState<boolean>('showCreateModal', () => false);
};

export const useOauthToken = () => {
  return useState<Loose>('oauthToken', () => ({}));
};

// 用户信息
export const useUserInfo = () => {
  const userInfo = useState<UserInfo | undefined>('userInfo', () => undefined);

  const { $sentry } = useNuxtApp();
  const sentry = $sentry as any;

  return computed<UserInfo | undefined>({
    get: () => {
      return userInfo.value;
    },
    set: val => {
      sentry?.setUser?.({ id: val?.user_id });
      sentry?.setTag?.('org', val?.organization_name);
      if (val) {
        yfxLogin(val);
      }
      userInfo.value = val;
    },
  });
};

// 用户发布授权信息
export const useUserPublishInfo = () => {
  return useState<UserPublishItem[]>('userPublishInfo', () => []);
};

// 用户登录信息
export function useSession() {
  const initSession = () => {
    return import.meta.client
      ? localGetItem(COOKIE_KEY.USER_TOKEN)
      : getCookie(COOKIE_KEY.USER_TOKEN);
  };

  const sessionState = useState(COOKIE_KEY.USER_TOKEN, initSession);

  // 使用 useState 来跟踪初始化状态，避免 SSR 中跨请求共享
  const sessionInit = useState('sessionInit', () => false);
  if (!sessionInit.value) {
    sessionInit.value = true;
    sessionState.value = initSession();
    // cdn有缓存，如果用户退出登录后，重新登录，session应该更新
  }

  const nuxtApp = useNuxtApp();

  return computed<Session | undefined>({
    get: () => {
      return sessionState.value;
    },
    set: val => {
      sessionState.value = val;
      const session = pick(val, ['user_id', 'key', 'session_id']) as Session;
      if (import.meta.client) {
        if (!isEmpty(session)) {
          localSetItem(COOKIE_KEY.USER_TOKEN, session);
        } else {
          localDeleteItem(COOKIE_KEY.USER_TOKEN);
        }
      } else {
        setCookie(
          COOKIE_KEY.USER_TOKEN,
          session,
          { httpOnly: true, maxAge: 60 * 60 * 24 * 30 },
          nuxtApp,
        );
      }
    },
  });
}

// 切换登录用户
export const useChangeUser = () => {
  const userInfo = useUserInfo();
  const accountList = useAccountList();
  const session = useSession();

  function setAccountList(res: LoginInfo[]) {
    accountList.value = res;
  }

  function setUserInfo(res: UserInfo) {
    userInfo.value = res;
  }

  async function setSession(res?: Session) {
    session.value = res;
    res ? await loginForWeb(res) : await logoutForWeb();
  }

  return {
    setAccountList,
    setUserInfo,
    setSession,
  };
};

export const useLoginOut = async (logoutBe: boolean = true) => {
  const session = useSession();
  const userInfo = useUserInfo();
  const accountList = useAccountList();

  const procs = [];
  if (session.value && logoutBe) {
    procs.push(logoutForBe());
  }
  procs.push(logoutForWeb());

  // 加快响应
  try {
    await Promise.allSettled(procs);
  } catch (error) {
    console.error(error);
  } finally {
    userInfo.value = undefined;
    accountList.value = [];
    session.value = undefined;
  }
};

// 更新当前用户信息
export const useUpdateUser = async () => {
  const userInfo = useUserInfo();
  const res = await getUserInfo();
  userInfo.value = res.data;
};

export const useUpdateAccounts = async () => {
  const accountList = useAccountList();
  const accounts = await getUserAccountList();
  accountList.value = accounts.data || [];
};

// 更新当前用户信息
export const useUpdateUserAndAccounts = async () => {
  await useUpdateUser();
  await useUpdateAccounts();
};

// 更新当前用户账号列表
export const useUpdateAccountList = async () => {
  const accountList = useAccountList();
  const accounts = await getUserAccountList();
  accountList.value = accounts.data || [];
};
